/**
 * The work order status machine (brief §4).
 *
 * Pure and dependency-free so it can be unit tested on its own and imported
 * by both the Worker and the SPA. Nothing writes `work_orders.status`
 * directly — every change goes through `assertTransition`, so an arbitrary
 * status write is impossible rather than merely discouraged.
 */

export const WORK_ORDER_STATUSES = [
  'draft',
  'pending_review',
  'dispatched',
  'acknowledged',
  'in_progress',
  'submitted',
  'approved',
  'closed',
  'rejected',
  'cancelled',
] as const;

export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending review',
  dispatched: 'Dispatched',
  acknowledged: 'Acknowledged',
  in_progress: 'In progress',
  submitted: 'Submitted',
  approved: 'Approved',
  closed: 'Closed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

/** Statuses from which a job can still be called off. */
export const PRE_APPROVAL_STATUSES = [
  'draft',
  'pending_review',
  'dispatched',
  'acknowledged',
  'in_progress',
  'submitted',
  'rejected',
] as const satisfies readonly WorkOrderStatus[];

/** No transition leaves these. */
export const TERMINAL_STATUSES = ['closed', 'cancelled'] as const satisfies readonly WorkOrderStatus[];

/**
 * The legal edges, exactly as drawn in brief §4. Deliberately no convenience
 * shortcuts (no draft → dispatched, no back-edges): if ops needs one, it
 * should be added here on purpose, with a test, not discovered in production.
 */
const TRANSITIONS: Record<WorkOrderStatus, readonly WorkOrderStatus[]> = {
  draft: ['pending_review', 'cancelled'],
  pending_review: ['dispatched', 'cancelled'],
  dispatched: ['acknowledged', 'cancelled'],
  acknowledged: ['in_progress', 'cancelled'],
  in_progress: ['submitted', 'cancelled'],
  submitted: ['approved', 'rejected', 'cancelled'],
  rejected: ['in_progress', 'cancelled'],
  approved: ['closed'],
  closed: [],
  cancelled: [],
};

export function isWorkOrderStatus(value: unknown): value is WorkOrderStatus {
  return typeof value === 'string' && (WORK_ORDER_STATUSES as readonly string[]).includes(value);
}

export function isTerminal(status: WorkOrderStatus): boolean {
  return (TERMINAL_STATUSES as readonly WorkOrderStatus[]).includes(status);
}

/** The statuses reachable from `from` in one step. */
export function allowedTransitions(from: WorkOrderStatus): readonly WorkOrderStatus[] {
  return TRANSITIONS[from];
}

export function canTransition(from: WorkOrderStatus, to: WorkOrderStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Thrown instead of writing an illegal status. Carries both ends for logging. */
export class InvalidStatusTransitionError extends Error {
  readonly from: WorkOrderStatus;
  readonly to: WorkOrderStatus;

  constructor(from: WorkOrderStatus, to: WorkOrderStatus) {
    const allowed = TRANSITIONS[from];
    const detail = allowed.length > 0 ? allowed.join(', ') : 'none (terminal state)';
    super(`Cannot move work order from "${from}" to "${to}". Allowed from "${from}": ${detail}.`);
    this.name = 'InvalidStatusTransitionError';
    this.from = from;
    this.to = to;
  }
}

export function assertTransition(from: WorkOrderStatus, to: WorkOrderStatus): void {
  if (!canTransition(from, to)) throw new InvalidStatusTransitionError(from, to);
}

/**
 * Column on `work_orders` stamped when a status is entered, so the detail
 * screen can show a timeline without replaying the audit log. Statuses that
 * are not milestones (`draft` is the creation state) map to null.
 */
export function timestampColumnFor(status: WorkOrderStatus): string | null {
  switch (status) {
    case 'pending_review':
      return 'review_requested_at';
    case 'dispatched':
      return 'dispatched_at';
    case 'acknowledged':
      return 'acknowledged_at';
    case 'in_progress':
      return 'started_at';
    case 'submitted':
      return 'submitted_at';
    case 'approved':
      return 'approved_at';
    case 'closed':
      return 'closed_at';
    case 'rejected':
      return 'rejected_at';
    case 'cancelled':
      return 'cancelled_at';
    case 'draft':
      return null;
  }
}
